/**
 * SHA-256 hashing utility for GTM user data.
 *
 * GA4 Enhanced Conversions requires user identifiers (email, phone)
 * to be hashed with SHA-256 before sending to the dataLayer.
 */

/**
 * Hash a string value using SHA-256.
 * Returns lowercase hex string, or undefined if input is empty/undefined.
 */
export async function sha256(
  value: string | undefined
): Promise<string | undefined> {
  if (!value) return undefined
  try {
    const encoder = new TextEncoder()
    // toLowerCase() is applied to all values before hashing:
    // - Required for email per GA4 Enhanced Conversions spec
    // - Harmless for phone numbers (digits are case-insensitive)
    const normalized = value.trim().toLowerCase()
    if (!normalized) return undefined
    const data = encoder.encode(normalized)
    const hashBuffer = await crypto.subtle.digest('SHA-256', data)
    const hashArray = Array.from(new Uint8Array(hashBuffer))
    return hashArray.map(b => b.toString(16).padStart(2, '0')).join('')
  } catch {
    return undefined
  }
}

/**
 * Build a hashed user object for GTM events.
 */
export async function buildHashedUser(params: {
  email?: string
  phone?: string
  country?: string
  medical_record_code?: string
}): Promise<{
  email_hash?: string
  phone_hash?: string
  country?: string
  medical_record_code?: string
}> {
  const [emailHash, phoneHash] = await Promise.all([
    sha256(params.email),
    sha256(params.phone)
  ])
  return {
    email_hash: emailHash,
    phone_hash: phoneHash,
    country: params.country,
    medical_record_code: params.medical_record_code
  }
}
